import { useParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { ExternalLink } from "lucide-react";

import data from "../data";
import { generateSchoolChannelUtmParams, SEP_BASE_URL } from "../lib/constants";
import { generateSchoolSEO } from "../lib/seo";
import { Button } from "../components/ui/button";
import { SEO } from "../components/SEO";
import Moved from "../components/WatchLive/WeveMoved";

export function SchoolPage() {
  const { id } = useParams<{ id: string }>();
  const [imageError, setImageError] = useState(false);

  // Helper function to get local image URL
  const getLocalImageUrl = (school: {
    logoUrl: string | null;
    slug: string;
  }) => {
    if (school.logoUrl) {
      return `/schools/${school.slug}.jpg`;
    }
    return null;
  };

  // Find the school by slug or ID (prioritize slug)
  const school = useMemo(() => {
    if (!id) return null;

    // Try to find by slug first (preferred)
    const schoolBySlug = data.find((school) => school.slug === id);
    if (schoolBySlug) return schoolBySlug;

    // Fall back to ID if slug not found
    const schoolById = data.find((school) => school.id.toString() === id);
    return schoolById;
  }, [id]);

  // Show not found if no id or school not found
  if (!id || !school) {
    return (
      <>
        <SEO
          title="School Not Found"
          description="The requested school could not be found. Browse our directory of high school sports streaming."
        />
        <div
          className="container mx-auto px-4 py-8"
          style={{ marginTop: "64px" }}
        >
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4 ">School Not Found</h1>
            <p className=" mb-4">
              {!id
                ? "No school ID was provided in the URL."
                : `The school with ID "${id}" could not be found.`}
            </p>
            <p className="text-gray-500 text-sm">
              Please check the URL or browse our school directory.
            </p>
            <div className="mt-4">
              <Button asChild className="rounded-xs">
                <a href="/">Browse All Schools</a>
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const schoolSEO = generateSchoolSEO(school);
  const utmButtonParams = generateSchoolChannelUtmParams(
    school.schoolName,
    "go_to_school"
  );
  const utmLinkParams = generateSchoolChannelUtmParams(
    school.schoolName,
    "check_it_out"
  );
  const sepGatewayButtonLink = `${SEP_BASE_URL}/gateway?id=${school.id}&source=localLive&page=channel&${utmButtonParams}`;
  const sepGatewayLink = `${SEP_BASE_URL}/gateway?id=${school.id}&source=localLive&page=channel&${utmLinkParams}`;

  return (
    <>
      <SEO
        title={schoolSEO.title}
        description={schoolSEO.description}
        keywords={schoolSEO.keywords}
        image={schoolSEO.image}
        type="article"
      />
      <div
        className="container mx-auto px-4 my-16"
        style={{ marginTop: "64px" }}
      >
        <div className="text-center">
          <div
            className="mx-auto mb-4 w-32 h-32 flex items-center justify-center bg-gray-100 rounded-lg"
            style={{ width: "128px", height: "128px" }}
          >
            {getLocalImageUrl(school) && !imageError ? (
              <img
                src={getLocalImageUrl(school)!}
                alt={school.schoolName}
                className="max-w-full max-h-full object-contain"
                loading="lazy"
                onError={() => {
                  setImageError(true);
                }}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <div className="text-4xl font-bold text-gray-400">
                {school.schoolName[0]?.toUpperCase() || "?"}
              </div>
            )}
          </div>

          <h1 className="text-3xl font-bold mb-2 ">{school.schoolName}</h1>
          <p className="text-lg  mb-4">
            {school.city}, {school.state}
          </p>

          <Button className="rounded-sm mb-12" asChild>
            <a href={sepGatewayButtonLink}>
              Go to school <ExternalLink />
            </a>
          </Button>

          <Moved sepGatewayLink={sepGatewayLink} />
        </div>
      </div>
    </>
  );
}
